import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const SPANISH_SPEAKING_COUNTRIES = [
  "MX", "ES", "AR", "CO", "PE", "VE", "CL", "EC", "GT", "CU",
  "BO", "DO", "HN", "PY", "SV", "NI", "CR", "PA", "UY", "GQ",
];

const GeoBanner = () => {
  const { i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const [targetLang, setTargetLang] = useState<"es" | "en">("es");

  useEffect(() => {
    const dismissed = localStorage.getItem("geo-banner-dismissed");
    if (dismissed) return;

    // Defer geo detection well past LCP so it never appears in the critical chain
    const schedule = (cb: () => void) =>
      'requestIdleCallback' in window
        ? (window as any).requestIdleCallback(cb, { timeout: 8000 })
        : setTimeout(cb, 5000);

    const idleId = schedule(() => {
      fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) })
      .then((res) => res.json())
      .then((data) => {
        const country = data?.country_code?.toUpperCase();
        if (!country) return;

        const currentLang = i18n.language?.startsWith("es") ? "es" : "en";
        const isSpanishCountry = SPANISH_SPEAKING_COUNTRIES.includes(country);

        if (isSpanishCountry && currentLang !== "es") {
          setTargetLang("es");
          setShow(true);
        } else if (!isSpanishCountry && currentLang !== "en") {
          setTargetLang("en");
          setShow(true);
        }
      })
      .catch(() => {
        // Silently fail — no banner shown
      });
    });

    return () => {
      if ('cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(idleId);
      }
    };
  }, [i18n.language]);

  const handleSwitch = () => {
    i18n.changeLanguage(targetLang);
    localStorage.setItem("geo-banner-dismissed", "true");
    setShow(false);
  };

  const handleDismiss = () => {
    localStorage.setItem("geo-banner-dismissed", "true");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed top-20 left-0 right-0 z-40 flex justify-center px-4 animate-in slide-in-from-top-2">
      <div className="bg-card border border-border rounded-sm px-5 py-3 flex items-center gap-4 shadow-xl max-w-lg">
        <p className="text-sm text-muted-foreground">
          {targetLang === "es"
            ? "Parece que estás en un país de habla hispana. ¿Ver en Español?"
            : "It looks like you're in an English-speaking country. View in English?"}
        </p>
        <button
          onClick={handleSwitch}
          className="px-4 py-1.5 bg-primary text-primary-foreground text-xs font-bold tracking-wider rounded-sm hover:bg-primary/90 transition-all whitespace-nowrap"
        >
          {targetLang === "es" ? "Español" : "English"}
        </button>
        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors text-lg leading-none"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default GeoBanner;
